import React, { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery } from "@mui/material";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import { AppBoxComponent } from "components/mui";

// am5.addLicense(import.meta.env.VITE_AMCHARTS_LICENSE_KEY);

const DonutChart = ({ data = [], height = 500 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useLayoutEffect(() => {
    // 🧹 Clean up existing root before creating new one
    const existingRoot = am5.registry.rootElements.find(
      (r) => r.dom.id === "donutChartDiv",
    );
    if (existingRoot) existingRoot.dispose();

    const root = am5.Root.new("donutChartDiv");
    // Removed Animated theme to prevent animations

    // 🟣 Chart setup
    const tooltip = am5.Tooltip.new(root, {
      labelText: '{category}: {value.formatNumber("#.00")}%',
    });

    // Configure tooltip font size for mobile
    const tooltipFontSize = isMobile ? 12 : 14;
    tooltip.label.set("fontSize", tooltipFontSize);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        innerRadius: am5.percent(40), // smaller hole = thicker donut
        layout: root.verticalLayout,
        tooltip: tooltip,
        animationDuration: 0, // No animation duration
      }),
    );

    // 🟢 Series setup
    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false,
        animationDuration: 0, // No animation duration
        animationType: "none", // Disable animation type
      }),
    );

    // 🎨 Slice styling
    series.slices.template.setAll({
      cornerRadius: 6,
      strokeWidth: 1,
      stroke: am5.color(0xffffff),
      tooltipText: '{category}: {value.formatNumber("#.00")}%',
      interactive: false, // 🚫 disables click/expand behavior
      expandOnClick: false, // 🚫 prevents slice expansion on click
      toggleKey: "none", // 🚫 disables keyboard toggle
      cursorOverStyle: "pointer",
    });

    // 🌈 Apply custom colors
    series.slices.template.adapters.add("fill", (fill, target) => {
      if (target.dataItem?.dataContext?.color) {
        return am5.color(target.dataItem.dataContext.color);
      }
      return fill;
    });

    // ❌ Hide labels and ticks
    series.labels.template.setAll({ forceHidden: true });
    series.ticks.template.setAll({ forceHidden: true });

    // 🧩 Load data
    series.data.setAll(data);

    // No animations - chart appears immediately without animation

    return () => {
      root.dispose();
    };
  }, [data, isMobile]);

  // 🧱 Chart container
  return (
    <AppBoxComponent
      id="donutChartDiv"
      sx={{
        width: "100%",
        maxWidth: "650px", // Increased size for better visibility
        height: `${height}px`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
      }}
    />
  );
};

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    }),
  ),
  height: PropTypes.number,
};

DonutChart.defaultProps = {
  data: [],
  height: 500,
};

export default DonutChart;
